import {
  Icon,
  Checkbox,
  Dropdown,
  Space,
  Card,
  Text,
  Divider,
  Button,
  message,
  Tooltip,
  theme,
} from 'components';
import {
  AppointmentConfigJsonType,
  ClientConfigContact,
} from '@bb/domain/dist/types/AppointmentConfig';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { sortBy } from 'lodash';
import errorReporter from '@app/errorReporter';
import { useBreakpoint } from 'hooks';
import { AppointmentDoc } from '@bb/domain/dist/schemas';
import type { JsonObject } from '@/app-package';
import { sendFeedbackToClients } from '../../../api/FeedbackController';
import { StyledTooltipButtonWrapper as TooltipButtonFix } from '../../../Router/pages/TourEditPage/StyledComponents';

type SendFeedbackButtonProps = {
  appointmentConfig: AppointmentConfigJsonType;
  appointment?: JsonObject<AppointmentDoc>;
  appointments?: JsonObject<AppointmentDoc>[];
  buttonStyle?: React.CSSProperties;
};

const SendFeedbackButton: React.FC<SendFeedbackButtonProps> = ({
  appointmentConfig,
  appointment,
  appointments,
  buttonStyle,
}) => {
  const { md } = useBreakpoint();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(
    new Set<string>(),
  );

  const clientContacts: ClientConfigContact[] = useMemo(
    () =>
      sortBy(
        appointmentConfig.contacts.filter(({ type }) =>
          ['Seller', 'Tenant', 'Landlord/Manager'].includes(type),
        ) as ClientConfigContact[],
        contact =>
          `${contact.firstName}${contact.lastName}`
            .replace(/\s/g, '')
            .toLowerCase(),
      ),
    [appointmentConfig.contacts],
  );

  useEffect(() => {
    setSelectedContactIds(
      new Set(clientContacts.map(({ contactId }) => contactId)),
    );
  }, [clientContacts]);

  const selectedContactIdsWithEmail = useMemo(() => {
    const contactIdsWithEmail = clientContacts
      .filter(({ email }) => Boolean(email))
      .map(({ contactId }) => contactId);
    return [...selectedContactIds].filter(contactId =>
      contactIdsWithEmail.includes(contactId),
    );
  }, [selectedContactIds, clientContacts]);

  const sendFeedback = async () => {
    setIsSending(true);
    try {
      await sendFeedbackToClients({
        listingId: appointmentConfig.listingId,
        appointmentIds: appointment
          ? [appointment.id]
          : appointments.map(({ id }) => id),
        contactIds: [...selectedContactIdsWithEmail],
      });
      message.success('Feedback sent');
      setIsDropdownVisible(false);
    } catch (err) {
      message.error('Feedback could not be sent. Please try again.');
      errorReporter.error(err);
    }
    setIsSending(false);
  };

  const contactsDropdownContent = (
    <StyledDropdownCard data-cy={'feedback-manual-send-receivers'}>
      <Space direction="vertical" size="small">
        <StyledScrollable>
          <Space direction="vertical" size="small">
            {clientContacts.map(contact => (
              <Tooltip
                title={!contact.email && 'User requires email.'}
                trigger={md ? 'hover' : 'click'}
                key={contact.contactId}
                placement="topLeft"
              >
                <StyledCheckBox
                  checked={selectedContactIds.has(contact.contactId)}
                  onChange={e => {
                    const newSelection = new Set(selectedContactIds);
                    if (e.target.checked) newSelection.add(contact.contactId);
                    else newSelection.delete(contact.contactId);
                    setSelectedContactIds(newSelection);
                  }}
                  data-cy={`send-feedback-select-${contact.contactId}`}
                  disabled={!contact.email}
                >
                  {`${contact.firstName} ${contact.lastName}`},
                  <Text type="secondary">{` ${contact.type}`}</Text>
                </StyledCheckBox>
              </Tooltip>
            ))}
          </Space>
        </StyledScrollable>
        <Divider />
        <StyledDropdownFooter>
          <Tooltip
            title={clientContacts.length <= 0 && 'No Listing Contacts'}
            trigger={md ? 'hover' : 'click'}
          >
            <TooltipButtonFix>
              <Button
                disabled={!selectedContactIdsWithEmail.length}
                loading={isSending}
                onClick={sendFeedback}
                type="primary"
                size="small"
                data-cy={'send-feedback-button'}
              >
                Send Feedback
              </Button>
            </TooltipButtonFix>
          </Tooltip>
        </StyledDropdownFooter>
      </Space>
    </StyledDropdownCard>
  );

  return (
    <>
      <Dropdown
        disabled={!clientContacts.length}
        dropdownRender={() => contactsDropdownContent}
        placement={md ? 'bottomRight' : 'bottomLeft'}
        trigger={['click']}
        // prevent dropdown from closing when clicking on items
        onOpenChange={setIsDropdownVisible}
        open={isDropdownVisible}
      >
        <StyledButton data-cy={'send-to-clients-button'} style={buttonStyle}>
          <Space direction="horizontal" align="center" size="small">
            <StyledSendIcon
              $disabled={
                (!appointment && !appointments?.length) ||
                !clientContacts.length
              }
            />
            {appointments?.length
              ? `Send ${appointments.length} feedback(s)`
              : `Send to Clients`}
          </Space>
        </StyledButton>
      </Dropdown>
    </>
  );
};

const StyledSendIcon = styled(Icon).attrs({
  type: 'antdSendOutlined',
  width: 12.6,
  height: 12.6,
})<{ $disabled: boolean }>`
  display: flex;
  .anticon > svg {
    color: ${props => (props.$disabled ? theme.disabledColor : 'white')};
  }
`;

const StyledButton = styled.span`
  margin: -1px;
  height: 30px;
  width: 133px;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledDropdownFooter = styled.div`
  display: flex;
  justify-content: end;

  ${Button} {
    border-radius: 2px;
  }
`;

const StyledDropdownCard = styled(Card)`
  padding: 10px 10px;
  ${Divider} {
    margin: 0;
  }
`;

const StyledCheckBox = styled(Checkbox)`
  && .ant-checkbox-disabled + span {
    color: #262626;
  }
`;

const StyledScrollable = styled.div`
  max-height: 35vh;
  overflow-y: auto;

  // magical fix for scrollbar flickering when selecting the last item.
  padding-bottom: 2px;
  margin-bottom: -2px;
`;

export default SendFeedbackButton;
